/**
 * The hover size strip on product cards.
 *
 * Picking a size adds that variant through the card's own quick add form
 * rather than posting to /cart/add directly: the theme then runs its normal
 * flow, which fires the events the cart icon and the cart drawer listen for,
 * so the drawer opens and its contents are current. Rebuilding that by hand
 * would mean re-dispatching the theme's internal events from the outside.
 *
 * For products with a Regular/Tall twin, both products' variants are embedded
 * in the card, so switching length re-renders the size row without a reload.
 */
(function () {
  function readData(strip) {
    var tag = strip.querySelector('[data-kalea-strip-data]');
    if (!tag) return null;
    try {
      var parsed = JSON.parse(tag.textContent);
      // Trailing null keeps the Liquid comma handling simple; drop it here.
      Object.keys(parsed).forEach(function (key) {
        parsed[key] = (parsed[key] || []).filter(Boolean);
      });
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /** The colour the card is currently showing, from its checked swatch. */
  function currentColor(strip) {
    var card = strip.closest('product-card');
    var checked = card && card.querySelector('input[name*="Color"]:checked, input[name*="color"]:checked');
    return checked ? checked.value : null;
  }

  function render(strip, which) {
    var data = readData(strip);
    var row = strip.querySelector('[data-kalea-sizes]');
    if (!data || !row) return;

    var all = data[which] || data.this || [];
    var color = currentColor(strip);

    // Only the sizes for the colour on screen -- otherwise the strip shows
    // another colour's stock and adds another colour's variant.
    var sizes = [];
    var seen = {};
    all.forEach(function (entry) {
      if (color && entry.color && entry.color !== color) return;
      if (seen[entry.size]) return;
      seen[entry.size] = true;
      sizes.push(entry);
    });

    row.textContent = '';

    sizes.forEach(function (size) {
      var el = document.createElement(size.available ? 'button' : 'span');
      el.className =
        'card-size-strip__size' + (size.available ? '' : ' card-size-strip__size--gone');
      el.textContent = size.size;
      if (size.available) {
        el.type = 'button';
        el.setAttribute('data-kalea-variant', String(size.id));
      }
      row.appendChild(el);
    });
  }

  function addToCart(strip, variantId) {
    var card = strip.closest('product-card');
    var form = card && card.querySelector('.quick-add__product-form-component');
    var input = form && form.querySelector('input[name="id"]');
    var button = form && form.querySelector('.quick-add__button--add');

    if (!input || !button) return false;

    input.value = variantId;
    input.disabled = false;

    // The theme disables this button when the card's own selected variant is
    // sold out -- which it is whenever the shopper picks a colour whose first
    // size is gone. The size being added here is known to be in stock, so the
    // button is re-enabled for the click.
    var wasDisabled = button.hasAttribute('disabled');
    if (wasDisabled) button.removeAttribute('disabled');

    // The drawer opens by itself only when the shop's "open cart drawer on
    // add" setting is on, and it is off here. Rather than turn that on for
    // the whole shop, it is opened for this one action: the listener goes on
    // immediately before the click, so the next cart:update is ours. The
    // timeout clears it if the add fails, so a later add is not hijacked.
    // A ring spins around the chosen size while the request is in flight.
    // Nothing visible happens otherwise: the add takes about a second, and
    // without it the click feels like it did not register.
    var pending = strip.querySelector('[data-kalea-variant="' + variantId + '"]');
    if (pending) pending.classList.add('is-loading');

    function clearPending() {
      if (pending) pending.classList.remove('is-loading');
    }

    var done = false;
    function openDrawer() {
      if (done) return;
      done = true;
      document.removeEventListener('cart:update', openDrawer);
      clearPending();
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }
    document.addEventListener('cart:update', openDrawer);
    setTimeout(function () {
      document.removeEventListener('cart:update', openDrawer);
      // If the add never reported back, the size must not be left spinning.
      clearPending();
    }, 8000);

    button.click();
    return true;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var strip = target.closest('[data-kalea-size-strip]');
    if (!strip) return;

    var lengthButton = target.closest('[data-kalea-length]');
    if (lengthButton) {
      event.preventDefault();
      strip.querySelectorAll('[data-kalea-length]').forEach(function (b) {
        b.classList.toggle('card-size-strip__size--current', b === lengthButton);
      });
      render(strip, lengthButton.getAttribute('data-kalea-length'));
      return;
    }

    var sizeButton = target.closest('[data-kalea-variant]');
    if (sizeButton) {
      event.preventDefault();
      addToCart(strip, sizeButton.getAttribute('data-kalea-variant'));
    }
  });

  function init() {
    document.querySelectorAll('[data-kalea-size-strip]').forEach(function (strip) {
      if (strip.hasAttribute('data-kalea-ready')) return;
      strip.setAttribute('data-kalea-ready', '');
      render(strip, 'this');
    });
  }

  /** Swapping the card's colour re-renders its strip. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var card = target.closest('product-card');
    if (!card) return;
    var strip = card.querySelector('[data-kalea-size-strip]');
    if (!strip) return;
    var active = strip.querySelector('[data-kalea-length].card-size-strip__size--current');
    render(strip, active ? active.getAttribute('data-kalea-length') : 'this');
  });

  init();
  // Cards arrive with pagination and section re-renders.
  new MutationObserver(init).observe(document.body, { childList: true, subtree: true });

  /**
   * Keeps the quick add sheet's buy button on the variant that is actually
   * selected.
   *
   * Picking a size in the sheet checks the radio but does not update the
   * form's hidden id field, so the sheet added the product's first variant
   * whatever the shopper chose. The variant is looked up from Shopify's own
   * product JSON for whichever product the sheet is showing, which also
   * covers the Regular/Tall swap.
   */
  var productCache = {};

  function productHandle(sheet) {
    var link = sheet.querySelector('.product-header a[href*="/products/"]');
    if (!link) return null;
    var path = link.getAttribute('href').split('?')[0];
    return decodeURIComponent(path.split('/products/')[1] || '');
  }

  async function syncVariant(sheet) {
    var handle = productHandle(sheet);
    if (!handle) return;

    if (!productCache[handle]) {
      try {
        productCache[handle] = await fetch('/products/' + encodeURIComponent(handle) + '.js').then(
          function (r) {
            return r.json();
          }
        );
      } catch (e) {
        return;
      }
    }

    var product = productCache[handle];
    var chosen = {};
    sheet.querySelectorAll('variant-picker input:checked').forEach(function (input) {
      var name = (input.getAttribute('name') || '').split('-')[0];
      if (name) chosen[name] = input.value;
    });

    var match = (product.variants || []).find(function (variant) {
      return (product.options || []).every(function (option, index) {
        var wanted = chosen[typeof option === 'string' ? option : option.name];
        return wanted == null || variant.options[index] === wanted;
      });
    });

    if (!match) return;

    var input = sheet.querySelector('input[name="id"]');
    if (!input) return;
    input.value = String(match.id);
    input.disabled = false;

    setButtonState(sheet, match.available !== false);
  }

  /**
   * The buy button's own state comes from the theme's component, which does
   * not follow the sheet after a Regular/Tall swap -- it kept saying "Add to
   * cart" for sizes the Tall product does not have in stock. Set from the
   * product data instead, the same source the variant id comes from.
   */
  function setButtonState(sheet, available) {
    var button = sheet.querySelector('.add-to-cart-button, button[type="submit"]');
    if (!button) return;

    var label =
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content');

    if (available) {
      button.removeAttribute('disabled');
      if (label) label.textContent = 'Add to cart';
    } else {
      button.setAttribute('disabled', '');
      if (label) label.textContent = 'Restocking soon';
    }
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var sheet = target.closest('#quick-add-modal-content');
    if (!sheet) return;
    if (!target.closest('variant-picker')) return;
    syncVariant(sheet);
  });

  /**
   * Buying from the quick add sheet.
   *
   * The theme's buy button inside the sheet does not read the form's hidden
   * id field -- it adds from the component's own state, which stays on the
   * product's first variant however the sheet is changed. Setting the field
   * was not enough: the sheet added XS whatever size was chosen, and after a
   * Regular/Tall swap it added the wrong product's variant.
   *
   * So the click is taken over here: the variant is resolved from the sheet's
   * own checked options against Shopify's product JSON, added directly, and
   * the drawer opened. The theme's handler is stopped so nothing is added
   * twice.
   */
  async function resolveVariantId(sheet) {
    var handle = productHandle(sheet);
    if (!handle) return null;

    if (!productCache[handle]) {
      try {
        productCache[handle] = await fetch('/products/' + encodeURIComponent(handle) + '.js').then(
          function (r) {
            return r.json();
          }
        );
      } catch (e) {
        return null;
      }
    }

    var product = productCache[handle];
    var chosen = {};
    sheet.querySelectorAll('variant-picker input:checked').forEach(function (input) {
      var name = (input.getAttribute('name') || '').split('-')[0];
      if (name) chosen[name] = input.value;
    });

    var match = (product.variants || []).find(function (variant) {
      return (product.options || []).every(function (option, index) {
        var wanted = chosen[typeof option === 'string' ? option : option.name];
        return wanted == null || variant.options[index] === wanted;
      });
    });

    // Never add something that is not in stock, whatever the button says.
    if (!match || match.available === false) return null;

    return match.id;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var sheet = target.closest('#quick-add-modal-content');
      if (!sheet) return;
      if (!target.closest('.add-to-cart-button, button[type="submit"]')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      resolveVariantId(sheet).then(function (variantId) {
        if (!variantId) return;

        // The drawer renders its contents server-side at page load and only
        // re-renders from the section HTML carried on the cart:update event.
        // Without it the drawer kept showing whatever it had when the page
        // loaded -- "your cart is empty" for a shopper who had not added
        // anything yet. Shopify returns the rendered section with the add
        // when it is asked for by id.
        var cartItems = document.querySelector('cart-items-component[data-section-id]');
        var sectionId = cartItems ? cartItems.dataset.sectionId : null;

        var body = { id: variantId, quantity: 1 };
        if (sectionId) body.sections = sectionId;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (result) {
            var dialog = document.querySelector('.quick-add-modal');
            if (dialog && dialog.open) dialog.close();

            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: result, data: { sections: result.sections || {} } }
              })
            );

            var drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          })
          .catch(function () {
            /* leave the sheet open so the shopper can try again */
          });
      });
    },
    true
  );

})();
